import z from 'zod';
import { UUIDSchema, UnixTimestampSchema } from '../common/schemas';
import { DBNumericSchema, DBTimestampSchema } from '../common/sql';
import { CategorisationSource } from './enums';

export const CategorisationConfidenceSchema = z.number().min(0).max(1).openapi({
  description: 'How sure the assigner was, 0 to 1',
  example: 0.82,
});

export const CategorisationSourceSchema = z.enum(CategorisationSource).openapi({
  description: 'Whether an engine or a person asserted this',
  example: CategorisationSource.Engine,
});

/**
 * DB Schemas
 *
 * Every assignment table carries the same columns beside its own value, so the
 * shared ones are written once and spread into each.
 */

const dbAssignmentColumns = {
  id: UUIDSchema,
  questionId: UUIDSchema,
  runId: UUIDSchema.nullable(),
  source: z.enum(CategorisationSource),
  isNegation: z.boolean(),
  confidence: DBNumericSchema.nullable(),
  assignedBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
};

export const DBQuestionTopicAssignmentSchema = z.object({
  ...dbAssignmentColumns,
  topicId: UUIDSchema,
});

export const DBQuestionTypeAssignmentSchema = z.object({
  ...dbAssignmentColumns,
  questionTypeId: UUIDSchema,
});

export const DBQuestionDirectiveVerbSchema = z.object({
  ...dbAssignmentColumns,
  directiveVerbId: UUIDSchema,
});

export const DBQuestionScaleScoreSchema = z.object({
  ...dbAssignmentColumns,
  scaleId: UUIDSchema,
  value: DBNumericSchema,
});

export const DBQuestionRelationshipSchema = z.object({
  ...dbAssignmentColumns,
  relatedQuestionId: UUIDSchema,
  relationshipTypeId: UUIDSchema,
});

/**
 * The resolution views carry no id and no negation: a negated value is absent
 * rather than present and false.
 */

const dbCurrentColumns = {
  questionId: UUIDSchema,
  source: z.enum(CategorisationSource),
  runId: UUIDSchema.nullable(),
  confidence: DBNumericSchema.nullable(),
  assignedBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
};

export const DBCurrentQuestionTopicSchema = z.object({
  ...dbCurrentColumns,
  topicId: UUIDSchema,
});

export const DBCurrentQuestionTypeSchema = z.object({
  ...dbCurrentColumns,
  questionTypeId: UUIDSchema,
});

export const DBCurrentQuestionDirectiveVerbSchema = z.object({
  ...dbCurrentColumns,
  directiveVerbId: UUIDSchema,
});

export const DBCurrentQuestionScaleScoreSchema = z.object({
  ...dbCurrentColumns,
  scaleId: UUIDSchema,
  value: DBNumericSchema,
});

export const DBCurrentQuestionRelationshipSchema = z.object({
  ...dbCurrentColumns,
  relatedQuestionId: UUIDSchema,
  relationshipTypeId: UUIDSchema,
});

/**
 * Client Schemas
 */

const assignmentFields = {
  id: UUIDSchema,
  questionId: UUIDSchema,
  runId: UUIDSchema.nullable(),
  source: CategorisationSourceSchema,
  isNegation: z.boolean(),
  confidence: CategorisationConfidenceSchema.nullable(),
  assignedBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
};

export const QuestionTopicAssignmentSchema = z.object({
  ...assignmentFields,
  topicId: UUIDSchema,
});

export const QuestionTypeAssignmentSchema = z.object({
  ...assignmentFields,
  questionTypeId: UUIDSchema,
});

export const QuestionDirectiveVerbSchema = z.object({
  ...assignmentFields,
  directiveVerbId: UUIDSchema,
});

export const QuestionScaleScoreSchema = z.object({
  ...assignmentFields,
  scaleId: UUIDSchema,
  value: z.number(),
});

export const QuestionRelationshipSchema = z.object({
  ...assignmentFields,
  relatedQuestionId: UUIDSchema,
  relationshipTypeId: UUIDSchema,
});

const currentFields = {
  questionId: UUIDSchema,
  source: CategorisationSourceSchema,
  runId: UUIDSchema.nullable(),
  confidence: CategorisationConfidenceSchema.nullable(),
  assignedBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
};

export const CurrentQuestionTopicSchema = z.object({
  ...currentFields,
  topicId: UUIDSchema,
});

export const CurrentQuestionTypeSchema = z.object({
  ...currentFields,
  questionTypeId: UUIDSchema,
});

export const CurrentQuestionDirectiveVerbSchema = z.object({
  ...currentFields,
  directiveVerbId: UUIDSchema,
});

export const CurrentQuestionScaleScoreSchema = z.object({
  ...currentFields,
  scaleId: UUIDSchema,
  value: z.number(),
});

export const CurrentQuestionRelationshipSchema = z.object({
  ...currentFields,
  relatedQuestionId: UUIDSchema,
  relationshipTypeId: UUIDSchema,
});

/** A question's categorisation as it stands, across every dimension. */
export const QuestionCategorisationSchema = z.object({
  questionId: UUIDSchema,
  topics: z.array(CurrentQuestionTopicSchema),
  questionTypes: z.array(CurrentQuestionTypeSchema),
  directiveVerbs: z.array(CurrentQuestionDirectiveVerbSchema),
  scaleScores: z.array(CurrentQuestionScaleScoreSchema),
  relationships: z.array(CurrentQuestionRelationshipSchema),
});
